import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, BookMarked, Star, X, Sparkles } from 'lucide-react';
import { SearchPicker } from '@/components/SearchPicker';
import NutritionPrograms from './NutritionPrograms';

const MEAL_CATEGORIES = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
const SLOTS = ['Meal 1', 'Meal 2', 'Meal 3', 'Snack'];

export default function AdminNutrition() {
  return (
    <div className="space-y-6" data-testid="admin-nutrition">
      <div>
        <div className="label-mini text-[#FFB800]">Library & Builder</div>
        <h1 className="font-display font-black text-4xl mt-1">Nutrition</h1>
      </div>
      <Tabs defaultValue="meals">
        <TabsList className="bg-regen-card border border-[var(--c-hair)]">
          <TabsTrigger value="meals" data-testid="nu-tab-meals">Meal Library</TabsTrigger>
          <TabsTrigger value="plans" data-testid="nu-tab-plans">Meal Plan Builder</TabsTrigger>
          <TabsTrigger value="templates" data-testid="nu-tab-templates">Meal Templates</TabsTrigger>
          <TabsTrigger value="programs" data-testid="nu-tab-programs">Programs</TabsTrigger>
        </TabsList>
        <TabsContent value="meals" className="mt-4"><MealLibrary /></TabsContent>
        <TabsContent value="plans" className="mt-4"><MealPlanBuilder /></TabsContent>
        <TabsContent value="templates" className="mt-4"><CoachTemplates /></TabsContent>
        <TabsContent value="programs" className="mt-4"><NutritionPrograms /></TabsContent>
      </Tabs>
    </div>
  );
}

function MealLibrary() {
  const [meals, setMeals] = useState([]);
  const [foods, setFoods] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [audit, setAudit] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [pendingApproval, setPendingApproval] = useState(null);
  const setApproval = async (m, approved) => {
    setPendingApproval(null);
    try {
      await api.patch(`/meals/${m.id}/approval`, { approved });
      setMeals(ms => ms.map(x => x.id === m.id ? { ...x, approved_by_jp: approved } : x));
      toast.success(approved ? `Approved “${m.name}” for the generator` : `“${m.name}” is no longer approved`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update approval'); }
  };
  const shownMeals = meals.filter(m => approvalFilter === 'all' || (approvalFilter === 'approved') === (m.approved_by_jp === true));
  const [form, setForm] = useState({ name: '', photo_url: '', calories: 0, protein_g: 0, fat_g: 0, carbs_g: 0, fiber_g: 0, ingredients: '', ingredient_items: [], instructions: '', category: 'Lunch' });

  const load = async () => { const r = await api.get('/meals'); setMeals(r.data); };
  const loadAudit = async () => { try { const r = await api.get('/admin/meals/audit'); setAudit(r.data); } catch { /* endpoint may be unavailable */ } };
  useEffect(() => { load(); loadAudit(); api.get('/foods').then(r => setFoods(r.data)).catch(() => {}); }, []);

  const removeAiMeals = async () => {
    const n = audit?.ai_generated_meals || 0;
    if (n <= 0) return;
    if (!confirm(`Remove ${n} AI-generated meal${n === 1 ? '' : 's'}?\n\nThis permanently deletes ONLY AI-generated meals (ai_generated = true). Your ${audit?.curated_meals ?? ''} Meal Library meals and ${audit?.library_copies ?? 0} client-plan copies of them will NOT be touched.`)) return;
    setCleaning(true);
    try {
      const r = await api.delete('/admin/meals/ai-generated?confirm=true');
      toast.success(`Removed ${r.data.deleted} AI meal${r.data.deleted === 1 ? '' : 's'} · ${r.data.curated_meals_after} curated meals intact`);
      await loadAudit(); await load();
    } catch { toast.error('Cleanup failed'); }
    finally { setCleaning(false); }
  };

  const openModal = (m) => {
    if (m) { setEditing(m.id); setForm({ ingredient_items: [], ...m }); } else { setEditing(null); setForm({ name: '', photo_url: '', calories: 0, protein_g: 0, fat_g: 0, carbs_g: 0, fiber_g: 0, ingredients: '', ingredient_items: [], instructions: '', category: 'Lunch' }); }
    setOpen(true);
  };
  const save = async () => {
    try {
      if (editing) await api.put(`/meals/${editing}`, form); else await api.post('/meals', form);
      toast.success('Saved'); setOpen(false); load();
    } catch { toast.error('Failed'); }
  };
  const del = async (id) => { if (!confirm('Delete?')) return; await api.delete(`/meals/${id}`); load(); };

  const ing = form.ingredient_items || [];
  const macroFromFood = (food, grams) => {
    const f = (grams || 0) / 100;
    return {
      calories: (food?.calories_per_100g || 0) * f,
      protein: (food?.protein_per_100g || 0) * f,
      carbs: (food?.carbs_per_100g || 0) * f,
      fat: (food?.fat_per_100g || 0) * f,
      fiber: (food?.fiber_per_100g || 0) * f,
    };
  };
  const liveTotals = ing.reduce((t, item) => {
    const food = foods.find(x => x.id === item.food_id);
    const m = macroFromFood(food, item.grams);
    return { calories: t.calories + m.calories, protein: t.protein + m.protein, carbs: t.carbs + m.carbs, fat: t.fat + m.fat, fiber: t.fiber + m.fiber };
  }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });
  const hasIng = ing.length > 0;

  const addIngredient = (food) => {
    if (!food?.id) return;
    setForm(f => ({ ...f, ingredient_items: [...(f.ingredient_items || []), { food_id: food.id, food_name: food.name, grams: food.serving_grams || 100 }] }));
  };
  const updIngredient = (idx, grams) => setForm(f => { const items = [...f.ingredient_items]; items[idx] = { ...items[idx], grams: parseFloat(grams) || 0 }; return { ...f, ingredient_items: items }; });
  const delIngredient = (idx) => setForm(f => ({ ...f, ingredient_items: f.ingredient_items.filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3" data-testid="meal-library-audit">
          <span className="text-sm text-muted-foreground">
            Curated Meal Library: <span className="font-semibold text-[var(--c-ink)]" data-testid="curated-count">{audit ? audit.curated_meals : meals.length}</span> meals
          </span>
          <Button
            onClick={removeAiMeals}
            disabled={cleaning || !audit || (audit?.ai_generated_meals || 0) === 0}
            data-testid="remove-ai-meals-btn"
            variant="outline"
            className="border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50 h-9"
            title={audit && audit.ai_generated_meals === 0 ? 'No AI-generated meals to remove' : 'Permanently delete AI-generated meals (curated meals untouched)'}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {cleaning ? 'Removing…' : `Remove AI-Generated Meals (${audit ? audit.ai_generated_meals : 0})`}
          </Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => openModal(null)} data-testid="add-meal-btn" className="bg-[#FFB800] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-2" /> Add Meal</Button></DialogTrigger>
          <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Meal</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="meal-name" className="bg-transparent border-[var(--c-hair)]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger className="bg-transparent border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{MEAL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Photo URL</Label><Input value={form.photo_url} onChange={e => setForm({...form, photo_url: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
              </div>
              {form.photo_url && <img src={form.photo_url} alt="preview" className="w-full max-h-40 object-cover rounded-lg" />}

              {/* Ingredient-based auto calculation */}
              <div className="rounded-xl border border-[var(--c-hair)] p-3 space-y-2" data-testid="meal-ingredient-builder">
                <div className="flex items-center justify-between">
                  <Label className="text-[#FFB800]">Ingredients — auto-calculates macros</Label>
                  {hasIng && <span className="text-[10px] font-mono text-muted-foreground">{Math.round(liveTotals.calories)} kcal · P{Math.round(liveTotals.protein)} C{Math.round(liveTotals.carbs)} F{Math.round(liveTotals.fat)} Fib{Math.round(liveTotals.fiber)}</span>}
                </div>
                <SearchPicker items={foods} onPick={addIngredient} allowFreeText={false} clearOnPick placeholder="Search food to add (e.g. Chicken Breast)…" testid="meal-ingredient-search" />
                {ing.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`meal-ingredient-${idx}`}>
                    <span className="text-sm flex-1 truncate">{item.food_name}</span>
                    <Input type="number" value={item.grams} onChange={e => updIngredient(idx, e.target.value)} data-testid={`meal-ingredient-grams-${idx}`} className="w-20 h-8 bg-transparent border-[var(--c-hair)] font-mono text-sm" />
                    <span className="text-xs text-muted-foreground">g</span>
                    <button onClick={() => delIngredient(idx)} data-testid={`meal-ingredient-del-${idx}`} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {!hasIng && <p className="text-[11px] text-muted-foreground">Add ingredients for automatic macros, or enter macros manually below.</p>}
              </div>

              <div className="grid grid-cols-5 gap-2">
                <div><Label>Cal</Label><Input type="number" value={hasIng ? Math.round(liveTotals.calories) : form.calories} disabled={hasIng} onChange={e => setForm({...form, calories: parseInt(e.target.value) || 0})} data-testid="meal-cal" className="bg-transparent border-[var(--c-hair)] font-mono disabled:opacity-70" /></div>
                <div><Label>P (g)</Label><Input type="number" step="0.1" value={hasIng ? Math.round(liveTotals.protein * 10) / 10 : form.protein_g} disabled={hasIng} onChange={e => setForm({...form, protein_g: parseFloat(e.target.value) || 0})} className="bg-transparent border-[var(--c-hair)] font-mono disabled:opacity-70" /></div>
                <div><Label>C (g)</Label><Input type="number" step="0.1" value={hasIng ? Math.round(liveTotals.carbs * 10) / 10 : form.carbs_g} disabled={hasIng} onChange={e => setForm({...form, carbs_g: parseFloat(e.target.value) || 0})} className="bg-transparent border-[var(--c-hair)] font-mono disabled:opacity-70" /></div>
                <div><Label>F (g)</Label><Input type="number" step="0.1" value={hasIng ? Math.round(liveTotals.fat * 10) / 10 : form.fat_g} disabled={hasIng} onChange={e => setForm({...form, fat_g: parseFloat(e.target.value) || 0})} className="bg-transparent border-[var(--c-hair)] font-mono disabled:opacity-70" /></div>
                <div><Label>Fib (g)</Label><Input type="number" step="0.1" value={hasIng ? Math.round(liveTotals.fiber * 10) / 10 : form.fiber_g} disabled={hasIng} onChange={e => setForm({...form, fiber_g: parseFloat(e.target.value) || 0})} data-testid="meal-fiber" className="bg-transparent border-[var(--c-hair)] font-mono disabled:opacity-70" /></div>
              </div>
              <div><Label>Notes / Recipe (optional)</Label><Textarea value={form.ingredients} onChange={e => setForm({...form, ingredients: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
              <div><Label>Instructions</Label><Textarea value={form.instructions} onChange={e => setForm({...form, instructions: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
              <Button onClick={save} data-testid="meal-save" className="w-full bg-[#FFB800] text-[#081726] font-semibold">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="meal-approval-filter">
        <span className="text-muted-foreground">Generator approval:</span>
        {[['all', `All (${meals.length})`], ['approved', `Approved (${meals.filter(m => m.approved_by_jp === true).length})`], ['unapproved', `Not approved (${meals.filter(m => m.approved_by_jp !== true).length})`]].map(([k, lbl]) => (
          <button key={k} onClick={() => setApprovalFilter(k)} data-testid={`meal-approval-filter-${k}`}
            className={`px-3 py-1 rounded-full border ${approvalFilter === k ? 'bg-[#FFB800] text-[#081726] border-[#FFB800]' : 'border-[var(--c-hair)]'}`}>{lbl}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shownMeals.map(m => (
          <div key={m.id} className="regen-card-elevated overflow-hidden group" data-testid={`meal-${m.id}`}>
            {m.photo_url ? <img src={m.photo_url} alt={m.name} className="w-full h-32 object-cover bg-[var(--c-film)]" /> : <div className="w-full h-32 bg-[var(--c-film)]" />}
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="label-mini text-[#FFB800]">{m.category}</div>
                <span data-testid={`meal-approval-badge-${m.id}`} className={`text-[10px] px-2 py-0.5 rounded-full ${m.approved_by_jp === true ? 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]' : 'bg-white/5 text-muted-foreground'}`}>{m.approved_by_jp === true ? 'Approved' : 'Not approved'}</span>
              </div>
              <div className="font-display font-bold mt-1">{m.name}</div>
              <div className="mt-2 text-xs text-muted-foreground font-mono">{m.calories} kcal · P{m.protein_g} C{m.carbs_g} F{m.fat_g}</div>
              {m.ingredient_items?.length > 0 && <div className="text-[10px] text-[#FFB800] mt-1">{m.ingredient_items.length} ingredients · auto</div>}
              <Button size="sm" variant="outline" onClick={() => setPendingApproval(m)} data-testid={`meal-approval-toggle-${m.id}`} className="mt-3 h-8 border-[var(--c-hair)] text-xs">
                {m.approved_by_jp === true ? 'Unapprove' : 'Approve for generator'}
              </Button>
              <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition">
                <Button size="sm" variant="outline" onClick={() => openModal(m)} className="border-[var(--c-hair)] h-8"><Edit2 className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(m.id)} className="border-red-500/30 text-red-400 h-8"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <ApprovalConfirm meal={pendingApproval} onCancel={() => setPendingApproval(null)}
        onConfirm={() => setApproval(pendingApproval, pendingApproval.approved_by_jp !== true)} />
    </div>
  );
}

function ApprovalConfirm({ meal, onCancel, onConfirm }) {
  const approving = meal?.approved_by_jp !== true;
  return (
    <AlertDialog open={!!meal} onOpenChange={o => !o && onCancel()}>
      <AlertDialogContent data-testid="meal-approval-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{approving ? 'Approve this meal?' : 'Unapprove this meal?'}</AlertDialogTitle>
          <AlertDialogDescription data-testid="meal-approval-confirm-text">
            {approving
              ? `“${meal?.name}” will be available to the nutrition generator and to client meal swaps.`
              : `“${meal?.name}” will no longer be used in new programmes or offered as a swap.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="meal-approval-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="meal-approval-confirm-ok">{approving ? 'Approve' : 'Unapprove'}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MealPlanBuilder() {
  const [plans, setPlans] = useState([]);
  const [meals, setMeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', items: [], is_template: true });

  const load = async () => {
    const p = await api.get('/meal-plans'); setPlans(p.data);
    const m = await api.get('/meals'); setMeals(m.data);
  };
  useEffect(() => { load(); }, []);

  const openModal = (p) => {
    if (p) { setEditing(p.id); setForm(p); } else { setEditing(null); setForm({ name: '', description: '', items: [], is_template: true }); }
    setOpen(true);
  };
  const setSlot = (slot, meal_id) => {
    const items = form.items.filter(i => i.slot !== slot);
    if (meal_id) items.push({ slot, meal_id });
    setForm({...form, items});
  };
  const save = async () => {
    try {
      if (editing) await api.put(`/meal-plans/${editing}`, form); else await api.post('/meal-plans', form);
      toast.success('Saved'); setOpen(false); load();
    } catch { toast.error('Failed'); }
  };
  const del = async (id) => { if (!confirm('Delete?')) return; await api.delete(`/meal-plans/${id}`); load(); };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => openModal(null)} data-testid="add-plan-btn" className="bg-[#FFB800] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-2" /> New Plan</Button></DialogTrigger>
          <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Meal Plan</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="plan-name" className="bg-transparent border-[var(--c-hair)]" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
              {SLOTS.map(slot => {
                const current = form.items.find(i => i.slot === slot);
                return (
                  <div key={slot}>
                    <Label>{slot}</Label>
                    <Select value={current?.meal_id || ''} onValueChange={v => setSlot(slot, v)}>
                      <SelectTrigger data-testid={`plan-${slot}`} className="bg-transparent border-[var(--c-hair)]"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" onSelect={() => setSlot(slot, null)}>— None —</SelectItem>
                        {meals.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.calories} kcal)</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
              <Button onClick={save} data-testid="plan-save" className="w-full bg-[#FFB800] text-[#081726] font-semibold">Save Plan</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans.map(p => (
          <div key={p.id} className="regen-card-elevated p-5" data-testid={`plan-${p.id}`}>
            <div className="font-display font-bold text-lg">{p.name}</div>
            <div className="text-xs text-muted-foreground mt-1">{p.items?.length || 0} slots filled</div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => openModal(p)} className="border-[var(--c-hair)]"><Edit2 className="w-3 h-3 mr-1.5" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={() => del(p.id)} className="border-red-500/30 text-red-400"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function CoachTemplates() {
  const [presets, setPresets] = useState([]);
  const [foods, setFoods] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [assigning, setAssigning] = useState(null); // preset being assigned
  const [form, setForm] = useState({ name: '', scope: 'coach', items: [], assigned_to: [] });
  const [picked, setPicked] = useState(null);
  const [grams, setGrams] = useState('');

  const load = async () => {
    const [p, f, c] = await Promise.all([api.get('/meal-presets'), api.get('/foods'), api.get('/clients')]);
    setPresets(p.data); setFoods(f.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const foodById = (id) => foods.find(f => f.id === id);
  const dbMatch = picked?.id ? picked : foods.find(f => f.name.toLowerCase() === (picked?.name || '').toLowerCase());
  const totals = () => {
    const t = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    form.items.forEach(it => { const f = foodById(it.food_id); if (!f) return; const g = (parseFloat(it.grams) || 0) / 100;
      t.calories += (f.calories_per_100g || 0) * g; t.protein += (f.protein_per_100g || 0) * g; t.carbs += (f.carbs_per_100g || 0) * g; t.fat += (f.fat_per_100g || 0) * g; });
    return t;
  };
  const addFood = () => {
    if (!dbMatch) return toast.error('Pick a food'); const g = parseFloat(grams) || 0; if (g <= 0) return toast.error('Enter grams');
    setForm({ ...form, items: [...form.items, { food_id: dbMatch.id, grams: g }] }); setPicked(null); setGrams('');
  };
  const openNew = () => { setForm({ name: '', scope: 'coach', items: [], assigned_to: [] }); setPicked(null); setGrams(''); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error('Name the template');
    if (form.items.length === 0) return toast.error('Add foods');
    await api.post('/meal-presets', { name: form.name.trim(), scope: form.scope, items: form.items.map(i => ({ food_id: i.food_id, grams: i.grams })), assigned_to: form.scope === 'coach' ? form.assigned_to : [] });
    toast.success('Template saved'); setOpen(false); load();
  };
  const del = async (id) => { if (!confirm('Delete template?')) return; await api.delete(`/meal-presets/${id}`); load(); };
  const saveAssign = async () => {
    await api.put(`/meal-presets/${assigning.id}/assign`, { client_ids: assigning.assigned_to });
    toast.success('Assigned'); setAssigning(null); load();
  };

  const SCOPE_COLOR = { coach: '#FF6FA8', unverified: '#FF5C5C', regen: '#00C896' };

  return (
    <div className="space-y-4" data-testid="coach-templates">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Coach templates are assigned to specific clients. "YourReGen Approved" meals come only from approved meals in the Meal Library.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="add-template-btn" className="bg-[#FFB800] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-2" /> New Template</Button></DialogTrigger>
          <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Meal Template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="High Protein Lunch" data-testid="template-name" className="bg-transparent border-[var(--c-hair)]" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.scope} onValueChange={v => setForm({ ...form, scope: v })}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="template-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coach">Coach Template (assign to clients)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Add Food</Label>
                <SearchPicker items={foods} value={picked?.name || ''} onPick={(v) => { setPicked(v); if (v?.serving_grams) setGrams(String(v.serving_grams)); }} placeholder="Search food…" testid="template-food-search" />
                {dbMatch && (
                  <div className="flex gap-2 mt-2">
                    <Input type="number" value={grams} onChange={e => setGrams(e.target.value)} placeholder="grams" data-testid="template-grams" className="bg-transparent border-[var(--c-hair)] font-mono flex-1" />
                    <Button onClick={addFood} data-testid="template-add-food" className="bg-[#FFB800] text-[#081726]"><Plus className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
              {form.items.length > 0 && (
                <div className="space-y-1.5">
                  {form.items.map((it, idx) => {
                    const f = foodById(it.food_id);
                    return (
                      <div key={idx} className="flex items-center justify-between rounded-lg bg-[var(--c-film)] p-2 text-sm" data-testid={`template-item-${idx}`}>
                        <span>{f?.name} · {it.grams}g</span>
                        <button onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}><X className="w-4 h-4 text-muted-foreground hover:text-red-400" /></button>
                      </div>
                    );
                  })}
                  <div className="text-xs font-mono text-muted-foreground text-right">{Math.round(totals().calories)} kcal · P{Math.round(totals().protein)} C{Math.round(totals().carbs)} F{Math.round(totals().fat)}</div>
                </div>
              )}
              {form.scope === 'coach' && (
                <div>
                  <Label>Assign to clients</Label>
                  <div className="max-h-40 overflow-y-auto space-y-1 mt-1 rounded-lg bg-[var(--c-film)] p-2">
                    {clients.map(c => (
                      <label key={c.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                        <input type="checkbox" checked={form.assigned_to.includes(c.id)} onChange={e => setForm({ ...form, assigned_to: e.target.checked ? [...form.assigned_to, c.id] : form.assigned_to.filter(x => x !== c.id) })} />
                        {c.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <Button onClick={save} data-testid="template-save" className="w-full bg-[#FFB800] text-[#081726] font-semibold">Save Template</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {presets.map(p => (
          <div key={p.id} className="regen-card-elevated p-4" data-testid={`coach-template-${p.id}`}>
            <div className="flex items-center gap-2">
              <BookMarked className="w-4 h-4" style={{ color: SCOPE_COLOR[p.scope] }} />
              <div className="font-display font-bold">{p.name}</div>
              <span className="pill text-[9px] ml-auto" style={{ background: `${SCOPE_COLOR[p.scope]}1a`, color: SCOPE_COLOR[p.scope] }}>{p.scope === 'unverified' ? 'Starter template — hidden from clients' : p.scope === 'regen' ? 'Your template (created by you)' : 'Coach'}</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1 truncate">{(p.items || []).map(i => `${i.food?.name} ${i.grams}g`).join(' · ')}</div>
            <div className="text-xs font-mono mt-2 flex gap-2">
              <span className="text-[#FFB800]">{Math.round(p.totals?.calories || 0)} kcal</span>
              <span className="text-[var(--c-green-ink)]">P{Math.round(p.totals?.protein || 0)}</span>
              <span className="text-[#00C8FF]">C{Math.round(p.totals?.carbs || 0)}</span>
              <span className="text-[#7DD3FC]">F{Math.round(p.totals?.fat || 0)}</span>
            </div>
            <div className="flex gap-2 mt-3">
              {p.scope === 'coach' && <Button size="sm" variant="outline" onClick={() => setAssigning({ ...p, assigned_to: p.assigned_to || [] })} className="border-[var(--c-hair)] h-8 text-xs" data-testid={`assign-template-${p.id}`}>Assign ({(p.assigned_to || []).length})</Button>}
              <Button size="sm" variant="outline" onClick={() => del(p.id)} className="border-red-500/30 text-red-400 h-8"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!assigning} onOpenChange={(o) => !o && setAssigning(null)}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-sm">
          <DialogHeader><DialogTitle>Assign "{assigning?.name}"</DialogTitle></DialogHeader>
          {assigning && (
            <div className="space-y-3">
              <div className="max-h-60 overflow-y-auto space-y-1 rounded-lg bg-[var(--c-film)] p-2">
                {clients.map(c => (
                  <label key={c.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                    <input type="checkbox" checked={assigning.assigned_to.includes(c.id)} onChange={e => setAssigning({ ...assigning, assigned_to: e.target.checked ? [...assigning.assigned_to, c.id] : assigning.assigned_to.filter(x => x !== c.id) })} />
                    {c.name}
                  </label>
                ))}
              </div>
              <Button onClick={saveAssign} data-testid="save-assign-btn" className="w-full bg-[#FFB800] text-[#081726] font-semibold">Save Assignment</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
